"use client";

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Play, Smartphone, Tablet, Monitor, Tv, CheckCircle2, Users, BookOpen, Heart } from "lucide-react";

const features = [
  {
    title: "Grow Spiritually",
    description: "Access thousands of Bible studies and teachings to deepen your faith journey.",
    icon: Heart,
  },
  {
    title: "Strengthen Your Family",
    description: "Find resources for couples, parents, and children to build stronger relationships.",
    icon: Users,
  },
  {
    title: "Weekly Encouragement",
    description: "Discover fresh content every week to keep you inspired and growing.",
    icon: BookOpen,
  },
];

const devices = [
  { name: "Phone", icon: Smartphone },
  { name: "Tablet", icon: Tablet },
  { name: "Computer", icon: Monitor },
  { name: "TV", icon: Tv },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: "easeOut",
    },
  },
  hover: {
    y: -8,
    transition: {
      duration: 0.3,
      ease: "easeOut",
    },
  },
};

export default function RightNowPage() {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  if (!isClient) {
    return (
      <div className="bg-background">
        <section className="container flex flex-col gap-10 pb-16 pt-24">
          <div className="flex-1 space-y-6 text-center">
            <h1 className="text-3xl font-bold leading-tight tracking-tight text-foreground sm:text-4xl lg:text-5xl">
              RightNow Media
            </h1>
            <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
              The Netflix of Christian Bible studies
            </p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-background via-background to-secondary/20">
      {/* Hero Section */}
      <section className="container relative overflow-hidden pb-20 pt-24 md:pt-32">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="relative z-10 mx-auto max-w-4xl text-center"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="mb-6"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-primary backdrop-blur-sm">
              <Play className="h-3 w-3" />
              Free for GKM Members
            </span>
          </motion.div>

          <motion.h1
            variants={itemVariants}
            className="mb-6 text-4xl font-bold leading-tight tracking-tight text-foreground sm:text-5xl lg:text-6xl"
          >
            RightNow Media
          </motion.h1>

          <motion.p
            variants={itemVariants}
            className="mb-4 text-xl font-medium text-muted-foreground sm:text-2xl"
          >
            The Netflix of Christian Bible studies
          </motion.p>

          <motion.p
            variants={itemVariants}
            className="mx-auto mb-10 max-w-2xl text-lg text-muted-foreground"
          >
            As a member of Global Kingdom Ministries, you have free access to thousands of
            faith-based videos and resources. Access RightNow Media on your phone, tablet,
            computer, or TV.
          </motion.p>

          <motion.div
            variants={itemVariants}
            className="flex flex-col items-center justify-center gap-4 sm:flex-row"
          >
            <Button
              asChild
              size="lg"
              className="group relative overflow-hidden px-8 py-6 text-base"
            >
              <a
                href="https://www.rightnowmedia.org/Account/Invite/globalkingdom"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Get Started for Free
                  <Play className="h-4 w-4 transition-transform group-hover:scale-110" />
                </span>
              </a>
            </Button>
          </motion.div>

          {/* Devices Section */}
          <motion.div
            variants={itemVariants}
            className="mt-16 flex flex-wrap items-center justify-center gap-8"
          >
            <p className="w-full text-sm font-medium uppercase tracking-wider text-muted-foreground">
              Available on all your devices
            </p>
            {devices.map((device) => {
              const Icon = device.icon;
              return (
                <div
                  key={device.name}
                  className="flex flex-col items-center gap-2 text-muted-foreground"
                >
                  <div className="rounded-xl bg-secondary/50 p-3">
                    <Icon className="h-6 w-6" />
                  </div>
                  <span className="text-xs font-medium">{device.name}</span>
                </div>
              );
            })}
          </motion.div>
        </motion.div>

        {/* Background decoration */}
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute -right-20 -top-20 h-96 w-96 rounded-full bg-primary/5 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 h-96 w-96 rounded-full bg-primary/5 blur-3xl" />
        </div>
      </section>

      {/* Video Section */}
      <section className="border-t border-border/50 bg-background/80 py-20 backdrop-blur-sm">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mx-auto max-w-5xl"
          >
            <div className="mb-8 text-center">
              <h2 className="mb-4 text-3xl font-bold text-foreground sm:text-4xl">
                See What RightNow Media Offers
              </h2>
              <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
                Watch this video to learn more about the amazing content available to you and your
                family.
              </p>
            </div>

            <div className="group relative overflow-hidden rounded-2xl border border-border/50 shadow-2xl transition-shadow hover:shadow-3xl">
              <div className="relative aspect-video w-full bg-black">
                <iframe
                  src="https://www.youtube.com/embed/Qw6s_AHyVpU?rel=0&modestbranding=1&playsinline=1"
                  title="RightNow Media Overview"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                  allowFullScreen
                  className="absolute inset-0 h-full w-full border-0"
                  loading="lazy"
                  style={{
                    aspectRatio: "16/9",
                  }}
                />
              </div>
              {/* Fullscreen hint for mobile */}
              <div className="pointer-events-none absolute bottom-4 right-4 rounded-lg bg-black/60 px-3 py-2 text-xs text-white opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100 md:hidden">
                Tap fullscreen to watch
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="border-t border-border/50 bg-background/80 py-20 backdrop-blur-sm">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
            className="mx-auto max-w-5xl"
          >
            <motion.div variants={itemVariants} className="mb-12 text-center">
              <h2 className="mb-4 text-3xl font-bold text-foreground sm:text-4xl">
                Everything You Need to Grow
              </h2>
              <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
                RightNow Media offers content for all ages and stages of life, helping you grow
                closer to God every day.
              </p>
            </motion.div>

            <div className="grid gap-8 md:grid-cols-3">
              {features.map((feature) => {
                const Icon = feature.icon;
                return (
                  <motion.article
                    key={feature.title}
                    variants={cardVariants}
                    whileHover="hover"
                    className="group relative overflow-hidden rounded-2xl border border-border/50 bg-gradient-to-br from-background to-secondary/30 p-8 shadow-lg transition-shadow hover:shadow-xl"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/0 to-primary/5 opacity-0 transition-opacity group-hover:opacity-100" />
                    <div className="relative space-y-4">
                      <div className="inline-flex rounded-xl bg-primary/10 p-3 transition-transform group-hover:scale-110">
                        <Icon className="h-6 w-6 text-primary" />
                      </div>
                      <h3 className="text-xl font-semibold text-foreground">{feature.title}</h3>
                      <p className="text-sm leading-relaxed text-muted-foreground">
                        {feature.description}
                      </p>
                    </div>
                  </motion.article>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="border-t border-border/50 bg-gradient-to-b from-secondary/20 to-background py-20">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mx-auto max-w-3xl rounded-3xl border border-primary/20 bg-gradient-to-br from-primary/10 via-primary/5 to-background p-12 text-center shadow-2xl backdrop-blur-sm"
          >
            <div className="mb-6 inline-flex rounded-full bg-primary/20 p-4">
              <CheckCircle2 className="h-8 w-8 text-primary" />
            </div>
            <h2 className="mb-4 text-3xl font-bold text-foreground sm:text-4xl">
              Ready to Get Started?
            </h2>
            <p className="mb-8 text-lg text-muted-foreground">
              Create your free RightNow Media account today and start exploring thousands of
              videos designed to help you grow in your faith.
            </p>
            <Button
              asChild
              size="lg"
              className="group relative overflow-hidden px-8 py-6 text-base"
            >
              <a
                href="https://www.rightnowmedia.org/Account/Invite/globalkingdom"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Join RightNow Media
                  <Play className="h-4 w-4 transition-transform group-hover:scale-110" />
                </span>
              </a>
            </Button>
            <p className="mt-6 text-sm text-muted-foreground">
              Completely free for Global Kingdom Ministries members
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
